import React, { useEffect, useState } from 'react';
import { Card, Space, Table, Tag, Input, Typography, Row, Col, Switch, Button, Popconfirm, Tooltip, message, notification } from 'antd';
import { InboxOutlined, PlayCircleOutlined, CheckCircleTwoTone, WarningOutlined, ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import MiniChart from '../../../shared/MiniChart';
import { useCampaignOrderQueueContext } from '../../../../core/components/campaigns/campaignordersqueue/useCampaignOrderQueueContext';
import {
  formatCurrency,
  formatNumber2SigFig,
  formatWholeNumber,
  getCTRFromAggregate,
} from '../../../../core/utils/campaigns';
import { isEmpty } from 'lodash';
import { Link } from 'react-router-dom';
import { formatCampaignName } from '../../../../core/utils/campaignNaming';
import { getPacingTone, getFlightPhase, PacingDot, PacingBar, PACING_TONES, TREND_ARROWS, formatCompactRemaining } from '../../Campaigns/shared/pacingVisuals';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import { createEnhancedSearchFilter } from '../../../../core/utils/enhancedSearch';
import { useMutation } from '@apollo/react-hooks';
import ARCHIVE_CAMPAIGN_ORDER from '../../../../core/GraphQl/Mutations/ARCHIVE_CAMPAIGN_ORDER';
import PAUSE_CAMPAIGN from '../../../../core/GraphQl/Mutations/PAUSE_CAMPAIGN';

const { Search } = Input;
const { Text } = Typography;

export const CampaignOrderLaunched = () => {
  const { timezone } = useTimezone();

  // Pacing 'classic view' toggle (DASHBOARD_PACING_UX_SPEC): preserves the
  // legacy 'Pace:' display for anyone whose workflow depends on it. Personal,
  // persisted in localStorage — instrument/remove if unused after a quarter.
  const [classicPacing, setClassicPacing] = useState(
    () => localStorage.getItem('pacingClassicView') === '1'
  );
  const toggleClassicPacing = () => {
    setClassicPacing((prev) => {
      localStorage.setItem('pacingClassicView', prev ? '0' : '1');
      return !prev;
    });
  };

  const columns = [
  {
    title: 'Campaign Details',
    dataIndex: 'name',
    key: 'name',
    sorter: true,
    render: (text, record) => (
      <Space size={4} direction="vertical">
        <Link to={`/campaigns/v2/orderqueue/details/${record.id}`}>
          {formatCampaignName(record, timezone)}
        </Link>
        <Space size={4}>
          <Tag color="blue">{(record.advertiser && record.advertiser.name) || ''}</Tag>
        </Space>
      </Space>
    ),
  },
  {
    title: 'Days Remaining',
    dataIndex: 'daysRemaining',
    key: 'daysRemaining',
    render: (text, record) => {
      const endDate = new Date(record.endDate);
      const today = new Date();
      if (endDate < today) {
        return <Text>0</Text>;
      }
      const diffTime = Math.abs(endDate - today);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return <Text>{diffDays.toString()}</Text>;
    },
  },
  {
    title: 'Start Date',
    dataIndex: 'startDate',
    key: 'startDate',
    sorter: true,
    render: (text) => moment(text).format('MM/DD/YYYY'),
  },
  {
    title: 'End Date',
    key: 'endDate',
    dataIndex: 'endDate',
    sorter: true,
    render: (text) => moment(text).format('MM/DD/YYYY'),
  },
  {
    title: 'Media Plan',
    key: 'hasMediaPlanStrategy',
    render: (_, record) => {
      const hasStrategy = record.campaigns?.some(campaign => campaign.hasMediaPlanStrategy);
      return hasStrategy ? (
        <Tag color="purple">Strategy</Tag>
      ) : null;
    },
  },
  {
    title: 'Impressions',
    key: 'impressions',
    dataIndex: 'impressions',
    render: (text, record) => (
      <MiniChart
        data={
          record.aggregateData && record.aggregateData.byDate ? record.aggregateData.byDate : []
        }
        yAxisLeft="impressions"
      />
    ),
  },
  {
    title: 'Performance',
    key: 'performance',
    dataIndex: 'performance',
    render: (text, record) => {
      const overAllData = record && record.aggregateData && record.aggregateData.overallData;
      return (
        <Space size={2} direction="vertical">
          <Text>Impressions</Text>
          <Text type="secondary">
            {overAllData ? parseInt(overAllData.aggImpressions).toFixed(0) : 0}
          </Text>
          <Text>Clicks</Text>
          <Text type="secondary">
            {overAllData ? parseInt(overAllData.aggClicks).toFixed(0) : 0}
          </Text>
          <Text>
            CTR: <Text type="secondary">{getCTRFromAggregate(overAllData ? overAllData : 0)}%</Text>
          </Text>
        </Space>
      );
    },
  },
  {
    title: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        Pacing
        {/* Classic escape hatch — same clock-icon pattern as the campaigns
            table header and the mobile card footers (one shared preference) */}
        <Tooltip title="Classic pacing view — legacy 'Pace' display" mouseEnterDelay={0.3}>
          <Button
            type="text"
            size="small"
            icon={<ClockCircleOutlined style={{ fontSize: 12 }} />}
            onClick={(e) => {
              e.stopPropagation();
              toggleClassicPacing();
            }}
            style={{
              padding: '0 4px',
              height: 22,
              color: classicPacing ? '#1890ff' : '#8c8c8c',
            }}
          />
        </Tooltip>
      </span>
    ),
    key: 'pacing',
    dataIndex: 'pacing',
    render: (text, record) => {
      const { cpm, budget, startDate, endDate, aggregateData, pacingData } = record;
      const chartData = aggregateData && aggregateData.byDate;
      if (!budget || !cpm) {
        return (
          <Space size={2} direction="vertical">
            <Text> N/A </Text>
          </Space>
        );
      }

      let impressionsSpent = 0;
      if (aggregateData && aggregateData.overallData) {
        impressionsSpent = aggregateData.overallData.aggImpressions;
      }

      let daysPast = 0;
      let dailyImpressionsGoal = 0;
      let impressionGoal = 0;
      if (cpm && budget && budget !== 1) {
        impressionGoal = (budget / cpm) * 1000;
      }

      const currentDate = moment()
        .utc() //Convert to UTC
        .startOf('D') //Start of UTC date
        .format(`YYYY-MM-DD`);

      const impressionsleft = impressionGoal - impressionsSpent;

      let todaysImpressions = 0;
      let impressionsUpToToday = 0;
      if (chartData && !isEmpty(chartData)) {
        //Today is in progress - So no need to count today
        chartData.forEach((chart) => {
          const { xAxis, impressions } = chart;
          const chartDate = moment(xAxis).utc().format(`YYYY-MM-DD`);

          if (chartDate !== currentDate) {
            impressionsUpToToday = impressionsUpToToday + impressions; //total of impressions up to today
            daysPast = daysPast + 1;
          } else {
            todaysImpressions = impressions;
          }
        });
      }

      const b = moment(startDate).utc();
      const a = moment(endDate).utc();
      let dayAdjust = 1;
      // currentDate is a formatted YYYY-MM-DD string — compare the same
      // format, not moment-vs-string (which coerces to an invalid compare).
      if (a.format('YYYY-MM-DD') <= currentDate) {
        //campaign is over
        dayAdjust = 0;
      }

      const daysOfFlight = a.diff(b, 'days');
      const daysLeft = daysOfFlight - daysPast + dayAdjust; //include today with date adjust

      if (daysLeft && impressionsleft) {
        dailyImpressionsGoal = impressionsleft / daysLeft;
      } else dailyImpressionsGoal = 0;

      // ── Pacing UX (DASHBOARD_PACING_UX_SPEC) ──────────────────────────────
      // pacingData.pace is a flight-END projection ((recent daily rate × days
      // left + delivered) ÷ flight goal), NOT a position. Frame it as a
      // projection, keep the number visible-but-muted while the flight is too
      // young for the rate to mean anything, and show the math on hover.
      // classicPacing preserves the legacy display verbatim.
      // Flight-window entry (today) is the source of truth, matching the
      // campaigns table — the scalar pacingData.pace can reflect the
      // order's window instead of the sub-campaign's flight. Classic view
      // keeps the scalar verbatim.
      const paceEntry = pacingData?.pacing?.[0];
      const projection = paceEntry?.pace ?? (pacingData ? pacingData.pace : null);
      // Shared ramping-window math (pacingVisuals.getFlightPhase) so the
      // chip fires identically on the table, card and queue — never for
      // classic view, never on day 0 / missing data.
      const {
        daysIntoFlight,
        flightLength,
        unstableDays,
        isRamping,
      } = getFlightPhase({
        daysIntoFlight:
          pacingData && pacingData.daysIntoFlight != null ? pacingData.daysIntoFlight : daysPast,
        daysLeft: pacingData && pacingData.daysLeft != null ? pacingData.daysLeft : daysLeft,
      });
      const flightTooYoung = !classicPacing && isRamping;
      const ratePerDay =
        (pacingData && (pacingData.dailyAvgImpressions || pacingData.impressionsYesterday)) ||
        todaysImpressions ||
        0;
      const neededPerDay = daysLeft > 0 ? impressionsleft / daysLeft : 0;
      const trend =
        pacingData && pacingData.paceYesterday != null && pacingData.paceSevenDaysAgo != null
          ? pacingData.paceYesterday > pacingData.paceSevenDaysAgo
            ? 'rising'
            : pacingData.paceYesterday < pacingData.paceSevenDaysAgo
              ? 'falling'
              : 'steady'
          : null;

      let textColor = '#ff4d4f'; // danger
      if (pacingData && pacingData.pace) {
        switch (true) {
          case pacingData.pace >= 95:
            textColor = '#52c41a'; // success
            break;
          case pacingData.pace >= 91 && pacingData.pace <= 95:
            textColor = '#faad14'; // warning
            break;
          default:
            break;
        }
      }
      // Age-gate the alarm color (new view only): a sub-91 projection is red
      // only once the flight is mature enough for it to be signal — red on
      // day 2 is noise, red on day 10 is signal. The new view renders this
      // via the calm palette (pacingVisuals): neutral ink + dot + bar.
      const pacingGracePeriod =
        !classicPacing &&
        projection != null &&
        projection < 91 &&
        daysIntoFlight < 10;
      const pacingTone = flightTooYoung
        ? PACING_TONES.neutral
        : getPacingTone(projection, { gracePeriod: pacingGracePeriod });

            const pacingTooltip = (
        <div style={{ minWidth: 220 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Day {daysIntoFlight} of {flightLength}</div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: 12,
              rowGap: 2,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <span style={{ opacity: 0.75 }}>Delivering</span>
            <span style={{ textAlign: 'right' }}>{formatWholeNumber(ratePerDay)}/day</span>
            <span style={{ opacity: 0.75 }}>Needed</span>
            <span style={{ textAlign: 'right' }}>{formatWholeNumber(neededPerDay)}/day</span>
            <span style={{ opacity: 0.75 }}>Days left</span>
            <span style={{ textAlign: 'right' }}>{daysLeft}</span>
            {trend && (
              <React.Fragment>
                <span style={{ opacity: 0.75 }}>Trend</span>
                <span style={{ textAlign: 'right' }}>{trend}</span>
              </React.Fragment>
            )}
          </div>
      {flightTooYoung && projection !== null && projection > 0 && (
        <div style={{ marginTop: 4 }}>
          Current unstable projection: {formatNumber2SigFig(projection)}% — extrapolated from
          {' '}{daysIntoFlight} day{daysIntoFlight === 1 ? '' : 's'} of data. Flight started
          {pacingData && pacingData.pacingStartDate ? ' ' + moment(pacingData.pacingStartDate).format('MMM D') : ' recently'}.
        </div>
      )}
          <div style={{ opacity: 0.7, marginTop: 6, fontSize: 12 }}>
            Projected finish = (recent rate × days left + delivered) ÷ flight goal
          </div>
        </div>
      );

      return (
        <Space size={2} direction="vertical">
          {classicPacing ? (
            <Text>
              Pace:{' '}
              <Text type="danger">
                <span style={{ color: textColor }}>
                  {pacingData && pacingData.pace !== null ? formatNumber2SigFig(pacingData.pace) : 0}%
                </span>
              </Text>
            </Text>
          ) : (
            <Space size={2} direction="vertical">
              {flightTooYoung && (
                <Tag
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    margin: 0,
                    marginBottom: 2,
                    fontSize: 11,
                    lineHeight: '16px',
                    color: '#595959',
                    background: '#fafafa',
                    borderColor: '#d9d9d9',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                  }}
                >
                  <ClockCircleOutlined style={{ fontSize: 11, flexShrink: 0 }} />
                  Ramping · estimate in {unstableDays - daysIntoFlight}d
                </Tag>
              )}
              <Tooltip title={pacingTooltip}>
                <Text>
                  <span style={{ color: '#595959' }}>Projected finish:</span>{' '}
                  <PacingDot tone={pacingTone} />
                  <span
                    style={{
                      color: flightTooYoung ? '#595959' : PACING_TONES.ink,
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {flightTooYoung
                      ? '—'
                      : `${projection !== null && projection !== undefined ? formatNumber2SigFig(projection) : 0}%`}
                  </span>
                  {trend && !flightTooYoung && (
                    <span style={{ marginLeft: 3, color: PACING_TONES.neutral }}>
                      {TREND_ARROWS[trend]}
                    </span>
                  )}
                </Text>
              </Tooltip>
              <div style={{ width: '100%', marginTop: 2 }}>
                <PacingBar
                  pace={projection}
                  tooYoung={flightTooYoung}
                  tone={pacingTone}
                />
              </div>
            </Space>
          )}
          <Text>
            {`${impressionsleft < 0 ? 'Overage:' : 'Remaining:'}`}{' '}
            <Text type="secondary">{`${classicPacing ? formatWholeNumber(impressionsleft) : formatCompactRemaining(impressionsleft)}`}</Text>
          </Text>
          <Text>
            Daily:{' '}
            <Text type="secondary">
              {dailyImpressionsGoal ? `${formatWholeNumber(dailyImpressionsGoal)}` : '0'}
            </Text>
          </Text>
          <Text>
            Today:{' '}
            <Text type="secondary">
              {todaysImpressions ? `${formatWholeNumber(todaysImpressions)}` : '0'}
            </Text>
          </Text>
        </Space>
      );
    },
  },
  {
    title: 'Sales',
    key: 'sales',
    dataIndex: 'sales',
    render: (text, record) => {
      const { aggregateData, cpm } = record;
      let impressions = 0;
      try {
        impressions = aggregateData.overallData.aggImpressions;
      } catch (e) {
        impressions = 0;
      }
      const budgetSpent = cpm * (impressions / 1000);

      return (
        <Row type="flex" justify="start" align="middle">
          <Col style={{ minWidth: '50px' }}>
            {!isEmpty(aggregateData) ? (
              <>
                <Row type="flex">
                  <Text>{`Orders:‏‏‎ ‎`}</Text>
                  <Text type="secondary">
                    {aggregateData &&
                    aggregateData.overallData &&
                    aggregateData.overallData.aggEvents &&
                    aggregateData.overallData.aggEvents.transactionsTotalCount
                      ? aggregateData.overallData.aggEvents.transactionsTotalCount
                      : 0}
                  </Text>
                </Row>
                <Row type="flex">
                  <Text>{`Rev:‏‏‎ ‎`}</Text>
                  <Text type="secondary">
                    {formatCurrency(
                      aggregateData &&
                        aggregateData.overallData &&
                        aggregateData.overallData.aggEvents &&
                        aggregateData.overallData.aggEvents.transactionTotalRevenue
                        ? parseFloat(aggregateData.overallData.aggEvents.transactionTotalRevenue)
                        : 0
                    )}
                  </Text>
                </Row>
                <Row type="flex">
                  <Text>{`ROAS:‏‏‎ ‎`}</Text>
                  <Text type="secondary">
                    {aggregateData.overallData.aggEvents.transactionTotalRevenue > 0
                      ? budgetSpent && budgetSpent > 0
                        ? (
                            parseFloat(
                              aggregateData.overallData.aggEvents.transactionTotalRevenue /
                                budgetSpent
                            ) * 100
                          ).toFixed(2) + '%'
                        : 'N/A'
                      : 'N/A'}
                  </Text>
                </Row>
              </>
            ) : (
              <Text> - </Text>
            )}
          </Col>
        </Row>
      );
    },
  },
  ];

  const {
    campaignOrders,
    loadingCampaignOrdersQueue,
    fetchCampaignOrdersQueue,
    pagination,
    setPagination,
  } = useCampaignOrderQueueContext();

  const [filterMediaPlanStrategy, setFilterMediaPlanStrategy] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [archivingIds, setArchivingIds] = useState(new Set());
  const [startingIds, setStartingIds] = useState(new Set());
  const [archiveCampaignOrder] = useMutation(ARCHIVE_CAMPAIGN_ORDER);
  const [pauseCampaign] = useMutation(PAUSE_CAMPAIGN);

  useEffect(() => {
    handleFetchCampaignOrdersQueue({
      skip: 0,
      first: pagination.pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFetchCampaignOrdersQueue = ({
    skip,
    first,
    name = '',
    sortField,
    sortOrder,
    hasMediaPlanStrategy = false,
  }) => {
    const userData = localStorage.getItem('userData');
    const orgId = userData ? JSON.parse(userData).role.org.id : null;
    const enhancedSearchFilter = createEnhancedSearchFilter(name);
    const whereConditions = [
      {
        status_in: ['LIVE', 'PAUSED'],
      },
    ];

    // Only add org filter if userData exists
    if (orgId) {
      whereConditions[0].orgs_some = {
        OR: [{ id: orgId }, { parentOrg_some: { id: orgId } }],
      };
    }

    if (enhancedSearchFilter) {
      whereConditions.push(enhancedSearchFilter);
    }

    // Filter by Has Media Plan Strategy
    if (hasMediaPlanStrategy) {
      whereConditions.push({
        campaigns_some: {
          hasMediaPlanStrategy: true,
        },
      });
    }

    fetchCampaignOrdersQueue({
      variables: {
        where: {
          AND: whereConditions,
        },
        orderBy:
          sortField && sortOrder ? `${sortField}_${sortOrder.toUpperCase()}` : 'updatedAt_DESC',
        skip,
        first,
      },
    });
  };

  const handleTableChange = ({ current, pageSize }, filters, sorter) => {
    const sortField = sorter.field;
    const sortOrder =
      sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;

    setPagination((prev) => ({ ...prev, current, pageSize }));
    handleFetchCampaignOrdersQueue({
      skip: (current - 1) * pageSize || 0,
      first: pageSize,
      sortField,
      sortOrder,
      name: searchValue,
      hasMediaPlanStrategy: filterMediaPlanStrategy,
    });
  };

  const handleSearchChange = (name) => {
    setSearchValue(name);
    handleFetchCampaignOrdersQueue({
      skip: 0,
      first: pagination.pageSize,
      name,
      hasMediaPlanStrategy: filterMediaPlanStrategy,
    });
  };

  const handleMediaPlanStrategyFilterChange = (checked) => {
    setFilterMediaPlanStrategy(checked);
    handleFetchCampaignOrdersQueue({
      skip: 0,
      first: pagination.pageSize,
      name: searchValue,
      hasMediaPlanStrategy: checked,
    });
  };

  const handleArchiveCampaignOrder = async (record) => {
    setArchivingIds((prev) => {
      const next = new Set(prev);
      next.add(record.id);
      return next;
    });

    try {
      await archiveCampaignOrder({
        variables: {
          id: record.id,
        },
      });

      message.success('Campaign order archived successfully');

      handleFetchCampaignOrdersQueue({
        skip: (pagination.current - 1) * pagination.pageSize,
        first: pagination.pageSize,
        name: searchValue,
        hasMediaPlanStrategy: filterMediaPlanStrategy,
      });
    } catch (error) {
      message.error('Failed to archive campaign order: ' + error.message);
    } finally {
      setArchivingIds((prev) => {
        const next = new Set(prev);
        next.delete(record.id);
        return next;
      });
    }
  };

  // Resume a paused campaign (PAUSE_CAMPAIGN is a toggle). Ported from the
  // former standalone Paused tab so the In Flight list can restart paused ads.
  const handleStartCampaign = async (record) => {
    const id = record.id;
    setStartingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    try {
      await pauseCampaign({ variables: { id } });
      notification.open({
        message: 'Successfully Started Campaign',
        icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
      });
      handleFetchCampaignOrdersQueue({
        skip: (pagination.current - 1) * pagination.pageSize,
        first: pagination.pageSize,
        name: searchValue,
        hasMediaPlanStrategy: filterMediaPlanStrategy,
      });
    } catch (err) {
      notification.open({
        message: `Failed to Start Campaign: ${err}`,
        icon: <WarningOutlined style={{ color: 'yellow' }} />,
      });
    } finally {
      setStartingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const columnsWithActions = [
    ...columns,
    {
      // In Flight merges LIVE + PAUSED — badge each row so the state is obvious.
      title: 'Status',
      key: 'flightStatus',
      width: 110,
      render: (_, record) => {
        if (record.dspConfig && record.dspConfig.status === 'ERROR') {
          return (
            <Tooltip title={record.dspConfig.error}>
              <Tag color="red">Error</Tag>
            </Tooltip>
          );
        }
        return record.status === 'PAUSED' ? (
          <Tag color="orange">Paused</Tag>
        ) : (
          <Tag color="green">Live</Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record) => (
        <Space>
          {record.status === 'PAUSED' && (
            <Tooltip title="Start Campaign Ads">
              <Popconfirm
                title="Start campaign ads?"
                onConfirm={() => handleStartCampaign(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  loading={startingIds.has(record.id)}
                  style={{ color: '#52c41a' }}
                />
              </Popconfirm>
            </Tooltip>
          )}
          <Popconfirm
            title="Are you sure you want to archive this campaign order?"
            description="This action cannot be undone. The campaign order will be moved to archived status."
            onConfirm={() => handleArchiveCampaignOrder(record)}
            okText="Yes, Archive"
            cancelText="Cancel"
            okType="danger"
          >
            <Button
              type="text"
              size="small"
              icon={<InboxOutlined />}
              loading={archivingIds.has(record.id)}
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="IN FLIGHT"
      extra={
        <Space size="middle">
          <Space size="small">
            <Switch
              checked={filterMediaPlanStrategy}
              onChange={handleMediaPlanStrategyFilterChange}
              size="small"
            />
            <span style={{ fontSize: '12px' }}>Has Media Plan Strategy</span>
          </Space>
          <Search
            placeholder="Search campaign"
            style={{
              width: 200,
            }}
            onSearch={(value) => handleSearchChange(value)}
            enterButton
            allowClear
          />
        </Space>
      }
    >
      <Table
        columns={columnsWithActions}
        dataSource={campaignOrders}
        loading={loadingCampaignOrdersQueue}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey={(record) => record.id}
      />
    </Card>
  );
};
